// @ts-nocheck
import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { exerciseService } from '../services/exerciseService';
import { ExerciseFormData } from '../types/exercise';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const scenarioTypes = [
  'Fire',
  'Flood', 
  'Active Threat',
  'Medical',
  'Cyber',
  'Other'
] as const;

const regulatoryOptions = [
  'CPS 230',
  'ISO 22301',
  'AS/NZS 5050',
  'NIST Cybersecurity Framework',
  'Other'
];

export const ExerciseForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  const { register, control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      title: '',
      scenarioType: 'Fire',
      description: '',
      objectives: [''],
      injects: [{ timeOffsetMinutes: 0, content: '' }],
      exerciseDate: '',
      exerciseTime: '',
      location: '',
      regulatoryTriggers: [],
      notes: ''
    }
  });

  const { 
    fields: objectiveFields, 
    append: appendObjective, 
    remove: removeObjective 
  } = useFieldArray({
    control,
    name: 'objectives'
  }) as any;

  const { 
    fields: injectFields, 
    append: appendInject, 
    remove: removeInject 
  } = useFieldArray({
    control,
    name: 'injects'
  }) as any;

  const onSubmit = async (data: any) => {
    if (!user) return;

    try {
      setError('');
      setLoading(true);

      // Filter out empty objectives
      const filteredData = {
        ...data,
        objectives: data.objectives.filter((obj: string) => obj.trim() !== ''),
        injects: data.injects.filter((inject: any) => inject.content.trim() !== '')
      };

      await exerciseService.createExercise(filteredData, user.uid, user.email || '');
      navigate('/');
    } catch (error) {
      setError('Failed to create exercise. Please try again.');
      console.error('Error creating exercise:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#2563eb',
        color: 'white',
        padding: '20px 0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
              style={{
                marginRight: '16px',
                backgroundColor: 'white',
                color: '#2563eb',
                border: '1px solid white',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ArrowLeft style={{ width: '16px', height: '16px', marginRight: '8px' }} />
              Back to Dashboard
            </Button>
            <div>
              <h1 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                margin: '0 0 4px 0'
              }}>Create New Exercise</h1>
              <p style={{
                fontSize: '16px',
                opacity: '0.9',
                margin: '0'
              }}>Design a crisis exercise for your team</p>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '32px 20px'
      }}>
        <form onSubmit={handleSubmit(onSubmit)} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px'
        }}>
          {error && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '12px 16px',
              borderRadius: '6px'
            }}>
              {error}
            </div>
          )}

          {/* Basic Information */}
          <Card style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
              <CardTitle style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#111827',
                margin: '0 0 8px 0'
              }}>Exercise Details</CardTitle>
              <CardDescription style={{
                color: '#6b7280',
                fontSize: '14px',
                margin: '0'
              }}>Basic information about your crisis exercise</CardDescription>
            </CardHeader>
            <CardContent style={{
              padding: '0 24px 24px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px'
                }}>
                  Exercise Title *
                </label>
                <Input
                  {...register('title', { required: 'Title is required' })}
                  placeholder="e.g., Q1 2024 Fire Evacuation Exercise"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                {errors.title && (
                  <p style={{
                    color: '#dc2626',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>{errors.title.message}</p>
                )}
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px'
                  }}>
                    Scenario Type *
                  </label>
                  <select
                    {...register('scenarioType', { required: 'Scenario type is required' })}
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    {scenarioTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.scenarioType && (
                    <p style={{
                      color: '#dc2626',
                      fontSize: '12px',
                      marginTop: '4px'
                    }}>{errors.scenarioType.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px'
                  }}>
                    Location
                  </label>
                  <Input
                    {...register('location')}
                    placeholder="e.g., Melbourne Office, Level 5"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px'
                  }}>
                    Exercise Date *
                  </label>
                  <Input
                    type="date"
                    {...register('exerciseDate', { required: 'Exercise date is required' })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.exerciseDate && (
                    <p style={{
                      color: '#dc2626',
                      fontSize: '12px',
                      marginTop: '4px'
                    }}>{errors.exerciseDate.message}</p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px'
                  }}>
                    Exercise Time *
                  </label>
                  <Input
                    type="time"
                    {...register('exerciseTime', { required: 'Exercise time is required' })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.exerciseTime && (
                    <p style={{
                      color: '#dc2626',
                      fontSize: '12px',
                      marginTop: '4px'
                    }}>{errors.exerciseTime.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px'
                }}>
                  Exercise Description
                </label>
                <textarea
                  {...register('description')}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  placeholder="Describe the exercise scenario and context..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Objectives */}
          <Card style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
              <CardTitle style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#111827',
                margin: '0 0 8px 0'
              }}>Learning Objectives</CardTitle>
              <CardDescription style={{
                color: '#6b7280',
                fontSize: '14px',
                margin: '0'
              }}>What should participants learn or practice?</CardDescription>
            </CardHeader>
            <CardContent style={{
              padding: '0 24px 24px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {objectiveFields.map((field: any, index: number) => (
                <div key={field.id} style={{ display: 'flex', gap: '8px' }}>
                  <Input
                    {...register(`objectives.${index}` as const)}
                    placeholder={`Objective ${index + 1}`}
                    style={{
                      flex: '1',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {objectiveFields.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removeObjective(index)}
                      style={{
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Trash2 style={{ width: '16px', height: '16px' }} />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => appendObjective('')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Plus style={{ width: '16px', height: '16px' }} />
                Add Objective
              </Button>
            </CardContent>
          </Card>

          {/* Injects */}
          <Card style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
              <CardTitle style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#111827',
                margin: '0 0 8px 0'
              }}>Exercise Injects</CardTitle>
              <CardDescription style={{
                color: '#6b7280',
                fontSize: '14px',
                margin: '0'
              }}>Timeline of events and information to be introduced during the exercise</CardDescription>
            </CardHeader>
            <CardContent style={{
              padding: '0 24px 24px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {injectFields.map((field: any, index: number) => (
                <div key={field.id} style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: '#f9fafb'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <h4 style={{
                      fontWeight: '500',
                      color: '#111827',
                      margin: '0'
                    }}>Inject {index + 1}</h4>
                    {injectFields.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => removeInject(index)}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '12px'
                        }}
                      >
                        <Trash2 style={{ width: '14px', height: '14px' }} />
                      </Button>
                    )}
                  </div>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '120px 1fr',
                    gap: '12px'
                  }}>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '4px'
                      }}>
                        Time (minutes)
                      </label>
                      <Input
                        type="number"
                        {...register(`injects.${index}.timeOffsetMinutes` as const, { 
                          valueAsNumber: true,
                          min: 0 
                        })}
                        placeholder="0"
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '4px'
                      }}>
                        Inject Content
                      </label>
                      <Input
                        {...register(`injects.${index}.content` as const)}
                        placeholder="e.g., Fire alarm activation in Building A, Level 3"
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => appendInject({ timeOffsetMinutes: 0, content: '' })}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Plus style={{ width: '16px', height: '16px' }} />
                Add Inject
              </Button>
            </CardContent>
          </Card>

          {/* Compliance & Notes */}
          <Card style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
              <CardTitle style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#111827',
                margin: '0 0 8px 0'
              }}>Compliance & Additional Notes</CardTitle>
              <CardDescription style={{
                color: '#6b7280',
                fontSize: '14px',
                margin: '0'
              }}>Regulatory frameworks and additional information</CardDescription>
            </CardHeader>
            <CardContent style={{
              padding: '0 24px 24px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Regulatory Triggers
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '8px'
                }}>
                  {regulatoryOptions.map((option) => (
                    <label key={option} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        value={option}
                        {...register('regulatoryTriggers')}
                        style={{
                          borderRadius: '4px',
                          border: '1px solid #d1d5db',
                          accentColor: '#2563eb'
                        }}
                      />
                      <span style={{ fontSize: '14px', color: '#374151' }}>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px'
                }}>
                  Additional Notes
                </label>
                <textarea
                  {...register('notes')}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  placeholder="Any additional information, preparation notes, or special considerations..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '16px',
            paddingTop: '16px'
          }}>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/')}
              style={{
                padding: '10px 20px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: loading ? '#9ca3af' : '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Save style={{ width: '16px', height: '16px' }} />
              {loading ? 'Creating...' : 'Create Exercise'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};
